import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/** Field edited via the TextEditPopup (project_main openTextChangingPopup). */
export type TextEditField = 'warranty' | 'submittal_cover_note';

/**
 * Project Board UI state (NOT owned by the RTK Query server cache).
 * Mirrors §6 "Slices" of PHASE3_PROJECTBOARD_INVENTORY.md (Part A) — 3a scope:
 * left rail collapse, General Info sub-tab, and the General Info modals.
 * (Sales/Timeline/Workflow modal flags arrive with their sub-phases.)
 */
export interface ProjectBoardState {
  /** #left_resizer checkbox — collapsed rail + footer `.to-left` reflow. */
  railCollapsed: boolean;

  /** General Info sub-tab ('project' | 'partners'); survives re-renders (parity 40). */
  generalInfoSubTab: 'project' | 'partners';

  modals: {
    /** ReassignPopup (#reassign_btn). */
    reassign: boolean;
    /** TextEditPopup — which contenteditable field is being edited, or null. */
    textEdit: { name: TextEditField } | null;
    /**
     * 3c — Workflow Manager popup (#workflow_popup), opened from the header
     * `#workflow_popup_button` AFTER a fresh project fetch (§1.9 / parity 99).
     */
    workflow: boolean;
  };

  /**
   * 3b — embedded AHU list (#main_ahus) UI state, inventory Part B §B1.
   * Mirrors the Backbone AhuView instance fields: clickedAHU,
   * expandedVersionParents (Set), duplicatedModelId, busy_ahus, the
   * `.duplicateAHU` right-column preloader, and the rename-in-place row.
   */
  ahus: {
    /** clickedAHU — the selected row driving the right info bar. */
    selectedAhuId: number | null;
    /** Version containers kept expanded across re-renders (B1.3 / parity 132). */
    expandedVersionParents: number[];
    /** Auto-select fallback for the socket post path (B1.8 / parity 137). */
    duplicatedModelId: number | null;
    /** busy_ahus — suppresses the socket delete echo (B1.8 / parity 150). */
    busyAhuIds: number[];
    /** `.duplicateAHU` dotted preloader on the right column. */
    rightPreloader: boolean;
    /** Row whose name input is unlocked for rename-in-place (B1.6). */
    renamingAhuId: number | null;
    modals: {
      /** Shared New-AHU popup (#new_ahu_popup, B1.5). */
      newAhu: boolean;
      /** DuplicatePopup — source AHU id, or null. */
      duplicateAhu: number | null;
      /** VersionPopup — source (main) AHU id, or null. */
      createVersion: number | null;
      /** DeletePopup — AHU id pending delete confirm, or null. */
      deleteAhu: number | null;
    };
  };
}

const initialState: ProjectBoardState = {
  railCollapsed: false,
  generalInfoSubTab: 'project',
  modals: {
    reassign: false,
    textEdit: null,
    workflow: false,
  },
  ahus: {
    selectedAhuId: null,
    expandedVersionParents: [],
    duplicatedModelId: null,
    busyAhuIds: [],
    rightPreloader: false,
    renamingAhuId: null,
    modals: {
      newAhu: false,
      duplicateAhu: null,
      createVersion: null,
      deleteAhu: null,
    },
  },
};

const projectBoardSlice = createSlice({
  name: 'projectBoard',
  initialState,
  reducers: {
    toggleRailCollapsed(state) {
      state.railCollapsed = !state.railCollapsed;
    },
    setGeneralInfoSubTab(state, action: PayloadAction<'project' | 'partners'>) {
      state.generalInfoSubTab = action.payload;
    },
    openReassign(state) {
      state.modals.reassign = true;
    },
    closeReassign(state) {
      state.modals.reassign = false;
    },
    openTextEdit(state, action: PayloadAction<TextEditField>) {
      state.modals.textEdit = { name: action.payload };
    },
    closeTextEdit(state) {
      state.modals.textEdit = null;
    },
    openWorkflow(state) {
      state.modals.workflow = true;
    },
    closeWorkflow(state) {
      state.modals.workflow = false;
    },

    // ---- 3b: AHU list (#main_ahus) ----
    selectAhu(state, action: PayloadAction<number | null>) {
      state.ahus.selectedAhuId = action.payload;
    },
    /** _toggleVersionsForMain — toggles a main's versions container (B1.3). */
    toggleVersionParent(state, action: PayloadAction<number>) {
      const id = action.payload;
      const i = state.ahus.expandedVersionParents.indexOf(id);
      if (i === -1) state.ahus.expandedVersionParents.push(id);
      else state.ahus.expandedVersionParents.splice(i, 1);
    },
    /** Create-version auto-expand of the parent (parity 132). */
    expandVersionParent(state, action: PayloadAction<number>) {
      if (!state.ahus.expandedVersionParents.includes(action.payload)) {
        state.ahus.expandedVersionParents.push(action.payload);
      }
    },
    setDuplicatedModelId(state, action: PayloadAction<number | null>) {
      state.ahus.duplicatedModelId = action.payload;
    },
    addBusyAhu(state, action: PayloadAction<number>) {
      if (!state.ahus.busyAhuIds.includes(action.payload)) {
        state.ahus.busyAhuIds.push(action.payload);
      }
    },
    removeBusyAhu(state, action: PayloadAction<number>) {
      state.ahus.busyAhuIds = state.ahus.busyAhuIds.filter(
        (id) => id !== action.payload
      );
    },
    setAhuRightPreloader(state, action: PayloadAction<boolean>) {
      state.ahus.rightPreloader = action.payload;
    },
    setRenamingAhu(state, action: PayloadAction<number | null>) {
      state.ahus.renamingAhuId = action.payload;
    },
    openNewAhu(state) {
      state.ahus.modals.newAhu = true;
    },
    closeNewAhu(state) {
      state.ahus.modals.newAhu = false;
    },
    openDuplicateAhu(state, action: PayloadAction<number>) {
      state.ahus.modals.duplicateAhu = action.payload;
      // openDuplicatePopup records duplicatedModelId = source id (B1.6).
      state.ahus.duplicatedModelId = action.payload;
    },
    closeDuplicateAhu(state) {
      state.ahus.modals.duplicateAhu = null;
    },
    openCreateVersion(state, action: PayloadAction<number>) {
      state.ahus.modals.createVersion = action.payload;
    },
    closeCreateVersion(state) {
      state.ahus.modals.createVersion = null;
    },
    openDeleteAhu(state, action: PayloadAction<number>) {
      state.ahus.modals.deleteAhu = action.payload;
    },
    closeDeleteAhu(state) {
      state.ahus.modals.deleteAhu = null;
    },
  },
});

export const {
  toggleRailCollapsed,
  setGeneralInfoSubTab,
  openReassign,
  closeReassign,
  openTextEdit,
  closeTextEdit,
  openWorkflow,
  closeWorkflow,
  selectAhu,
  toggleVersionParent,
  expandVersionParent,
  setDuplicatedModelId,
  addBusyAhu,
  removeBusyAhu,
  setAhuRightPreloader,
  setRenamingAhu,
  openNewAhu,
  closeNewAhu,
  openDuplicateAhu,
  closeDuplicateAhu,
  openCreateVersion,
  closeCreateVersion,
  openDeleteAhu,
  closeDeleteAhu,
} = projectBoardSlice.actions;

export default projectBoardSlice.reducer;
